import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Alert, AlertDescription } from '../ui/alert';
import { User, Shield, History, Check, X, AlertTriangle, RefreshCw } from 'lucide-react';

export const AccountInfoPanel = ({ adminData, formatDate }) => (
  <Card>
    <CardHeader>
      <CardTitle className="flex items-center gap-2">
        <User className="w-5 h-5 text-red-600" />
        Account Information
      </CardTitle>
      <CardDescription>
        Your current admin account details
      </CardDescription>
    </CardHeader>
    <CardContent className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
          <Label className="text-gray-500 text-sm">Email Address</Label>
          <p className="text-lg font-medium text-gray-900 dark:text-white mt-1">
            {adminData?.email}
          </p>
        </div>
        <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
          <Label className="text-gray-500 text-sm">Name</Label>
          <p className="text-lg font-medium text-gray-900 dark:text-white mt-1">
            {adminData?.name || 'Admin'}
          </p>
        </div>
        <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
          <Label className="text-gray-500 text-sm">Role</Label>
          <p className="text-lg font-medium text-gray-900 dark:text-white mt-1 capitalize">
            {adminData?.role}
          </p>
        </div>
        <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
          <Label className="text-gray-500 text-sm">Last Login</Label>
          <p className="text-lg font-medium text-gray-900 dark:text-white mt-1">
            {formatDate(adminData?.last_login)}
          </p>
        </div>
      </div>

      <Alert className="bg-blue-50 dark:bg-blue-900/20 border-blue-200">
        <Shield className="w-4 h-4 text-blue-600" />
        <AlertDescription className="text-blue-800 dark:text-blue-200">
          For security, you can update your email and password from the tabs above.
          All changes require your current password for verification.
        </AlertDescription>
      </Alert>
    </CardContent>
  </Card>
);

export const LoginHistoryPanel = ({ loginHistory, fetchLoginHistory, historyLoading, formatDate }) => (
  <Card>
    <CardHeader className="flex flex-row items-center justify-between">
      <div>
        <CardTitle className="flex items-center gap-2">
          <History className="w-5 h-5 text-red-600" />
          Login History
        </CardTitle>
        <CardDescription>
          Recent login activity on your account
        </CardDescription>
      </div>
      <Button
        variant="outline"
        size="sm"
        onClick={fetchLoginHistory}
        disabled={historyLoading}
      >
        <RefreshCw className={`w-4 h-4 mr-2 ${historyLoading ? 'animate-spin' : ''}`} />
        {historyLoading ? 'Refreshing...' : 'Refresh'}
      </Button>
    </CardHeader>
    <CardContent>
      {loginHistory.length === 0 ? (
        <p className="text-gray-500 text-center py-8">No login history available</p>
      ) : (
        <div className="space-y-3">
          {loginHistory.map((log) => (
            <div
              key={log.id || `${log.timestamp}-${log.action}`}
              className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-lg"
            >
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                  log.action === 'login'
                    ? 'bg-green-100 text-green-600'
                    : log.action === 'logout'
                    ? 'bg-gray-100 text-gray-600'
                    : 'bg-red-100 text-red-600'
                }`}>
                  {log.action === 'login' ? (
                    <Check className="w-5 h-5" />
                  ) : log.action === 'logout' ? (
                    <X className="w-5 h-5" />
                  ) : (
                    <AlertTriangle className="w-5 h-5" />
                  )}
                </div>
                <div>
                  <p className="font-medium text-gray-900 dark:text-white capitalize">
                    {log.action.replace('_', ' ')}
                  </p>
                  <p className="text-sm text-gray-500">
                    {log.ip_address || 'IP not recorded'}
                  </p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {formatDate(log.timestamp)}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}
    </CardContent>
  </Card>
);
